import { osnovaApi } from '../base';
import type {
  ProgressInfo,
  ProgressMetrics,
  ProgressRun,
  ProgressRunDecision,
  ProgressScope,
  ProgressScopeOptions,
} from '@entities/progress/types';
import type {
  AttendanceView,
  ObjectDashboard,
  ObjectProfile,
  ObjectProfileUpdate,
  ObjectProfileView,
  ProgressObjectItem,
} from '@entities/progress/dashboard';
import type {
  CreatedInstruction,
  InstructionRecipient,
  NewInstruction,
  ProgressInstruction,
} from '@entities/progress/instructions';
import type {
  AdvisorAnswer,
  AdvisorAskInput,
  AdvisorMessage,
  AdvisorSession,
  AdvisorSpend,
  AdvisorUsage,
} from '@entities/progress/advisor';

const ADVISOR = '/agents/progress/advisor';

export const progressApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    /** Карточка агента + перечень модулей витрины. */
    getProgressInfo: b.query<ProgressInfo, void>({
      query: () => '/agents/progress/info',
      providesTags: ['Progress'],
    }),

    /** Метрики агента (всего отчётов, завершённых, открытых). */
    getProgressMetrics: b.query<ProgressMetrics, void>({
      query: () => '/agents/progress/metrics',
      providesTags: ['Progress'],
    }),

    /* ── Дашборд объекта (главный экран) ─────────────────────────────────── */

    /** Объекты для пикера дашборда + отметка о сохранённых привязках. */
    listProgressObjects: b.query<ProgressObjectItem[], void>({
      query: () => '/agents/progress/objects',
      providesTags: ['ProgressObject'],
    }),

    /** Все показатели объекта сразу (синхронно, без LLM). */
    getProgressObjectDashboard: b.query<ObjectDashboard, string>({
      query: (id) => `/agents/progress/objects/${id}/dashboard`,
      providesTags: (_r, _e, id) => [{ type: 'ProgressObject', id }],
    }),

    /**
     * Люди на объекте (Хронум) для баннера: все площадки холдинга и отметка,
     * какие относятся к объекту. Отдельно от дашборда — баннер стоит выше
     * пикера объектов и работает до выбора объекта.
     */
    getProgressChronum: b.query<AttendanceView, string | undefined>({
      query: (object) => ({
        url: '/agents/progress/chronum',
        params: object ? { object } : undefined,
      }),
      providesTags: ['ProgressObject'],
    }),

    /** Привязки объекта для формы: сохранённые значения, договоры, ленты ПА. */
    getProgressObjectProfile: b.query<ObjectProfileView, string>({
      query: (id) => `/agents/progress/objects/${id}/profile`,
      providesTags: (_r, _e, id) => [{ type: 'ProgressObject', id }],
    }),

    /** Сохранить привязки объекта; дашборд и список объектов пересчитываются. */
    saveProgressObjectProfile: b.mutation<ObjectProfile, { id: string; body: ObjectProfileUpdate }>(
      {
        query: ({ id, body }) => ({
          url: `/agents/progress/objects/${id}/profile`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: ['ProgressObject'],
      },
    ),

    /* ── Поручения по показателям ──────────────────────────────────────── */

    /** Кандидаты в исполнители: учётки + справочник сотрудников. */
    listProgressRecipients: b.query<InstructionRecipient[], void>({
      query: () => '/agents/progress/recipients',
    }),

    /** Реестр поручений руководителя (фильтры: объект, статус, `overdue`). */
    listProgressInstructions: b.query<
      ProgressInstruction[],
      { project_id?: string; status?: string }
    >({
      query: (params) => ({ url: '/agents/progress/instructions', params }),
      providesTags: ['ProgressInstruction'],
    }),

    /** Выдать поручение: сервер снимает показатель, пишет реестр, шлёт уведомления. */
    createProgressInstruction: b.mutation<CreatedInstruction, NewInstruction>({
      query: (body) => ({ url: '/agents/progress/instructions', method: 'POST', body }),
      invalidatesTags: ['ProgressInstruction'],
    }),

    /** Отменить выданное поручение. */
    cancelProgressInstruction: b.mutation<ProgressInstruction, string>({
      query: (id) => ({ url: `/agents/progress/instructions/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['ProgressInstruction'],
    }),

    /** «Мои поручения» — для любого сотрудника, без прав на агента. */
    listMyInstructions: b.query<ProgressInstruction[], void>({
      query: () => '/instructions/my',
      providesTags: ['ProgressInstruction'],
    }),

    /** Отметить выполненным (исполнитель или руководитель). */
    completeInstruction: b.mutation<ProgressInstruction, { id: string; note: string | null }>({
      query: ({ id, note }) => ({
        url: `/instructions/${id}/done`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: ['ProgressInstruction'],
    }),

    /** Документ поручения (DOCX) как Blob — лениво, по клику. */
    downloadInstructionDocx: b.query<Blob, string>({
      query: (id) => ({
        url: `/instructions/${id}/docx`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /** Опции для формы запуска отчёта: проекты (обязательны) + опц. сметы и серии
     *  совещаний для привязки источников плана/статусов. */
    getProgressScopeOptions: b.query<ProgressScopeOptions, void>({
      query: () => '/agents/progress/scope/options',
      providesTags: ['Progress'],
    }),

    /** Список прогонов агента. Статус меняется асинхронно — страница включает
     *  polling через `{ pollingInterval }` пока есть created/processing. */
    listProgressRuns: b.query<ProgressRun[], void>({
      query: () => '/agents/progress/runs',
      providesTags: ['ProgressRun'],
    }),

    /** Карточка прогона: прогон + снимок (KPI) + пояснительная записка. */
    getProgressRun: b.query<ProgressRun, string>({
      query: (id) => `/agents/progress/runs/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'ProgressRun', id }],
    }),

    /** Запустить формирование отчёта по области (JSON `ProgressScope`,
     *  `project_id` обязателен). Возвращает созданный прогон. */
    createProgressRun: b.mutation<ProgressRun, ProgressScope>({
      query: (body) => ({ url: '/agents/progress/runs', method: 'POST', body }),
      invalidatesTags: ['ProgressRun'],
    }),

    /** HITL: решение по прогону (accepted | rejected | disputed). Возвращает
     *  обновлённый прогон; инвалидируем его карточку. */
    decideProgressRun: b.mutation<ProgressRun, { id: string; decision: ProgressRunDecision }>({
      query: ({ id, decision }) => ({
        url: `/agents/progress/runs/${id}/decision`,
        method: 'PATCH',
        body: { decision },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'ProgressRun', id }],
    }),

    /** Скачать DOCX сводного отчёта как Blob. RBAC — Authorization ставит
     *  baseQuery. `no-store` — документ мог быть пересобран. Лениво
     *  (`useLazyExportProgressRunQuery`): запрос по клику. */
    exportProgressRun: b.query<Blob, string>({
      query: (id) => ({
        url: `/agents/progress/runs/${id}/download`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    /* ── ОСНОВА.Эксперт (чат руководителя) ─────────────────────────────── */

    /** Расход и лимит текущего руководителя за месяц. */
    getAdvisorUsage: b.query<AdvisorUsage, void>({
      query: () => `${ADVISOR}/usage`,
      providesTags: ['AdvisorUsage'],
    }),

    /** Свои чаты, недавно активные сверху. */
    listAdvisorSessions: b.query<AdvisorSession[], void>({
      query: () => `${ADVISOR}/sessions`,
      providesTags: ['AdvisorSession'],
    }),

    /** Реплики своего чата по порядку. */
    listAdvisorMessages: b.query<AdvisorMessage[], string>({
      query: (id) => `${ADVISOR}/sessions/${id}/messages`,
      providesTags: (_r, _e, id) => [{ type: 'AdvisorMessages', id }],
    }),

    /** Вопрос с файлами (multipart). Без `session_id` сервер заводит новый чат
     *  после ответа. Ответ сразу кладём в кэш реплик и расхода — без лишнего
     *  перечитывания. Вопрос сохраняется только вместе с ответом: при ошибке
     *  черновик остаётся у пользователя в поле ввода. */
    askAdvisor: b.mutation<AdvisorAnswer, AdvisorAskInput>({
      query: ({ session_id, question, files }) => {
        const fd = new FormData();
        fd.append('question', question);
        if (session_id) fd.append('session_id', session_id);
        for (const f of files) fd.append('files', f, f.name);
        return { url: `${ADVISOR}/ask`, method: 'POST', body: fd };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const pair = [data.question, data.answer];
          // Новый чат: кэша реплик ещё нет — кладём готовым, без загрузки.
          if (!arg.session_id) {
            void dispatch(
              progressApi.util.upsertQueryData('listAdvisorMessages', data.session.id, pair),
            );
          } else {
            dispatch(
              progressApi.util.updateQueryData('listAdvisorMessages', data.session.id, (draft) => {
                draft.push(...pair);
              }),
            );
          }
          void dispatch(progressApi.util.upsertQueryData('getAdvisorUsage', undefined, data.usage));
        } catch {
          /* ошибку показывает экран через unwrap() */
        }
      },
      invalidatesTags: (r) => (r ? ['AdvisorSession'] : []),
    }),

    /** Файл, созданный Экспертом (только свой чат). */
    downloadAdvisorFile: b.query<Blob, string>({
      query: (id) => ({
        url: `${ADVISOR}/files/${id}`,
        cache: 'no-store',
        responseHandler: (response) => response.blob(),
      }),
    }),

    renameAdvisorSession: b.mutation<{ ok: boolean }, { id: string; title: string }>({
      query: ({ id, title }) => ({
        url: `${ADVISOR}/sessions/${id}`,
        method: 'PATCH',
        body: { title },
      }),
      invalidatesTags: ['AdvisorSession'],
    }),

    deleteAdvisorSession: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `${ADVISOR}/sessions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdvisorSession'],
    }),

    /** Администратору: расход руководителей за месяц и их лимиты. */
    listAdvisorSpend: b.query<AdvisorSpend[], void>({
      query: () => `${ADVISOR}/spend`,
      providesTags: ['AdvisorUsage'],
    }),

    /** Администратору: личный месячный лимит; `null` — общий. */
    setAdvisorLimit: b.mutation<{ ok: boolean }, { userId: string; limitUsd: number | null }>({
      query: ({ userId, limitUsd }) => ({
        url: `${ADVISOR}/limits/${userId}`,
        method: 'PUT',
        body: { limit_usd: limitUsd },
      }),
      invalidatesTags: ['AdvisorUsage'],
    }),

    /* ── Обновления «Что нового» (информационные баннеры) ─────────────────── */
  }),
  overrideExisting: false,
});

export const {
  useListProgressRecipientsQuery,
  useListProgressInstructionsQuery,
  useCreateProgressInstructionMutation,
  useCancelProgressInstructionMutation,
  useListMyInstructionsQuery,
  useCompleteInstructionMutation,
  useLazyDownloadInstructionDocxQuery,
  useListProgressObjectsQuery,
  useGetProgressObjectDashboardQuery,
  useGetProgressChronumQuery,
  useGetProgressObjectProfileQuery,
  useSaveProgressObjectProfileMutation,
  useGetProgressInfoQuery,
  useGetProgressMetricsQuery,
  useGetProgressScopeOptionsQuery,
  useListProgressRunsQuery,
  useGetProgressRunQuery,
  useCreateProgressRunMutation,
  useDecideProgressRunMutation,
  useLazyExportProgressRunQuery,
  useGetAdvisorUsageQuery,
  useListAdvisorSessionsQuery,
  useListAdvisorMessagesQuery,
  useAskAdvisorMutation,
  useLazyDownloadAdvisorFileQuery,
  useRenameAdvisorSessionMutation,
  useDeleteAdvisorSessionMutation,
  useListAdvisorSpendQuery,
  useSetAdvisorLimitMutation,
} = progressApi;
